import React from 'react';
import Helmet from 'react-helmet';
import { useIntl, defineMessages } from '@kyruus/intl';

const globalMessages = defineMessages({
  globalSiteName: {
    id: 'global.site.name',
    description: 'Global site name for SEO purposes',
    defaultMessage: ''
  },
  globalAlternateSiteName: {
    id: 'global.site.alternate_name',
    description:
      'Global alternate site names for SEO purposes, comma-separated',
    defaultMessage: ''
  }
});

/**
 * Wrapper for `react-helmet` that allows for internationalized titles and meta tags.
 * Sets global site name and alternate names in meta tags if defined in global messages.
 *
 * @param {Object} props - Props for the component.
 * @param {string|Object} props.title - The title of the page, either a string or a message descriptor.
 * @param {Object} [props.titleValues] - Values for the title message descriptor.
 * @param {Array<Object>} [props.meta] - Array of meta tag objects, each with a `message` and optional `values`.
 * @param {Array<Object>} [props.link] - Array of link tag objects.
 * @returns The rendered Helmet component with internationalized title and meta tags.
 */
export function IntlHelmet({ title, titleValues, meta = [], link, ...props }) {
  const intl = useIntl();

  const resolvedTitle =
    typeof title === 'string' ? title : intl.formatMessage(title, titleValues);

  const globalSiteName = intl.formatMessage(globalMessages.globalSiteName);
  const globalAlternateNameString = intl.formatMessage(
    globalMessages.globalAlternateSiteName
  );
  const globalAlternateNames =
    globalAlternateNameString &&
    globalAlternateNameString !== globalMessages.globalAlternateSiteName.id
      ? globalAlternateNameString
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean)
      : [];

  const resolvedMeta = [
    ...(globalSiteName && globalSiteName !== globalMessages.globalSiteName.id
      ? [
          { itemprop: 'name', content: globalSiteName },
          { property: 'og:site_name', content: globalSiteName }
        ]
      : []),
    ...globalAlternateNames.map((name) => ({
      itemprop: 'alternateName',
      content: name
    })),
    ...meta.map(({ message, values, ...rest }) =>
      message ? { ...rest, content: intl.formatMessage(message, values) } : rest
    )
  ];

  return (
    <Helmet {...props} title={resolvedTitle} meta={resolvedMeta} link={link} />
  );
}
